import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import Navbar from "./Navbar";

function AuthForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Redirect if already logged in
  useEffect(() => {
    if (currentUser) {
      console.log("User already authenticated, checking onboarding status");
      checkOnboardingStatus();
    }
  }, [currentUser]);

  const checkOnboardingStatus = async () => {
    if (!currentUser) return;

    try {
      const userRef = doc(db, "users", currentUser.uid);
      const docSnap = await getDoc(userRef);

      if (docSnap.exists()) {
        const userData = docSnap.data();
        if (userData.onboardingCompleted) {
          navigate("/dashboard");
        } else {
          navigate("/onboarding");
        }
      } else {
        navigate("/onboarding");
      }
    } catch (error) {
      console.error("Error checking onboarding status:", error);
      navigate("/onboarding");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      let userCredential;

      if (isLogin) {
        userCredential = await signInWithEmailAndPassword(auth, email, password);
        const uid = userCredential.user.uid;
        const userRef = doc(db, "users", uid);
        const docSnap = await getDoc(userRef);

        if (docSnap.exists()) {
          const userData = docSnap.data();
          if (userData.onboardingCompleted) {
            navigate("/dashboard");
          } else {
            navigate("/onboarding");
          }
        } else {
          navigate("/onboarding");
        }
      } else {
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
        // Don't create user document here - let onboarding handle it
        navigate("/onboarding");
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError("");
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const uid = result.user.uid;

      const userRef = doc(db, "users", uid);
      const snap = await getDoc(userRef);

      if (snap.exists()) {
        const userData = snap.data();
        if (userData.onboardingCompleted) {
          navigate("/dashboard");
        } else {
          navigate("/onboarding");
        }
      } else {
        // New user - send to onboarding
        navigate("/onboarding");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-100 to-red-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-lg w-full space-y-10">
          {/* Header */}
          <div className="text-center">
            <Link to="/" className="flex flex-col sm:flex-row items-center justify-center mb-8 sm:mb-10 group">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-red-500 to-orange-500 rounded-full blur-lg opacity-30 animate-pulse"></div>
                <img
                  src="/korlish.png"
                  alt="Korlish Logo"
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full shadow-2xl group-hover:shadow-red-500/25 transition-all duration-300 ring-4 ring-white relative z-10"
                />
                <div className="absolute -top-2 -right-2 bg-red-600 text-white text-sm font-bold px-3 py-1 rounded-full animate-bounce">
                  🇰🇷
                </div>
              </div>
              <div className="sm:ml-4 mt-2 sm:mt-0 text-center sm:text-left">
                <h1 className="text-3xl sm:text-4xl font-black bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 bg-clip-text text-transparent">
                  Korlish
                </h1>
                <p className="text-sm text-red-600 font-bold">한국어 학습</p>
              </div>
            </Link>

            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-4">
              {isLogin ? "Welcome back!" : "Join Korlish"}
            </h2>
            <div className="mb-6">
              <p className="text-2xl text-red-700 font-black mb-2">
                {isLogin ? "다시 오신 것을 환영합니다!" : "Korlish에 가입하세요!"}
              </p>
              <p className="text-lg text-gray-600 font-semibold">
                {isLogin ? "(Welcome back!)" : "(Join Korlish!)"}
              </p>
            </div>
            <p className="text-xl text-gray-800 font-semibold">
              {isLogin
                ? "Sign in to continue your Korean learning journey"
                : "Start your Korean learning adventure today"
              }
            </p>
          </div>

          {/* Form */}
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-2xl p-10 border-4 border-red-200">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="sr-only">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none relative block w-full px-4 py-3 border-4 border-red-200 placeholder-gray-500 text-gray-900 rounded-2xl focus:outline-none focus:ring-red-300 focus:border-red-400 transition-colors duration-300 text-lg font-semibold bg-white/80"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="appearance-none relative block w-full px-4 py-3 border-4 border-red-200 placeholder-gray-500 text-gray-900 rounded-2xl focus:outline-none focus:ring-red-300 focus:border-red-400 transition-colors duration-300 text-lg font-semibold bg-white/80"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-lg font-bold rounded-2xl text-white bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <svg className="animate-spin h-5 w-5 text-white mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : isLogin ? (
                    "Sign In"
                  ) : (
                    "Sign Up"
                  )}
                </button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gradient-to-br from-red-50 to-orange-50 text-gray-500">
                    Or continue with
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 border-4 border-red-200 rounded-2xl shadow-lg bg-white text-gray-700 font-bold hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
              >
                <img src="/google-logo.png" alt="Google" className="w-6 h-6 mr-3" />
                Continue with Google
              </button>
            </div>

            {/* Toggle */}
            <div className="mt-8 text-center">
              <p className="text-lg text-gray-700 font-semibold">
                {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="font-black text-red-600 hover:text-orange-600 transition-colors duration-200"
                >
                  {isLogin ? "Sign up" : "Sign in"}
                </button>
              </p>
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
              Why join Korlish?
            </h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <span className="text-green-600 mr-4 text-lg font-bold">✓</span>
                <span className="text-base text-gray-800 font-medium">Track your learning progress</span>
              </div>
              <div className="flex items-center">
                <span className="text-green-600 mr-4 text-lg font-bold">✓</span>
                <span className="text-base text-gray-800 font-medium">Access to all lessons and vocabulary</span>
              </div>
              <div className="flex items-center">
                <span className="text-green-600 mr-4 text-lg font-bold">✓</span>
                <span className="text-base text-gray-800 font-medium">Practice with interactive quizzes</span>
              </div>
              <div className="flex items-center">
                <span className="text-green-600 mr-4 text-lg font-bold">✓</span>
                <span className="text-base text-gray-800 font-medium">Learn Korean culture and traditions</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AuthForm;